import { BasePick, Sport } from '../domain/types';

type EspnTeam = { id?: string; abbreviation?: string; name?: string; displayName?: string; shortDisplayName?: string; location?: string; logos?: { href?: string }[] };
type TeamRow = { team?: EspnTeam };
type RosterItem = { id?: string; fullName?: string; displayName?: string; headshot?: { href?: string } | string; items?: RosterItem[] };
export type PlayerImages = { photo?: string; teamLogo?: string; athleteId?: string };
const ESPN = 'https://site.api.espn.com/apis/site/v2/sports';
const tidy = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim().replace(/\s+(jr|sr|ii|iii|iv|v)$/i, '');
const sportPath = (sport: Sport) => sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
const cacheKey = (sport: Sport, player: string) => `rushprops.playerImages.v4.${sport}.${tidy(player)}`;
const memory = new Map<string, PlayerImages>();
const teamsCache = new Map<Sport, Promise<TeamRow[]>>();
const rostersCache = new Map<string, Promise<RosterItem[]>>();

async function teams(sport: Sport): Promise<TeamRow[]> {
  let pending = teamsCache.get(sport);
  if (!pending) {
    pending = fetch(`${ESPN}/${sportPath(sport)}/teams?limit=100`).then(r => r.ok ? r.json() : Promise.reject()).then(data => data.sports?.[0]?.leagues?.[0]?.teams ?? []);
    teamsCache.set(sport, pending);
  }
  return pending;
}
async function roster(sport: Sport, id: string): Promise<RosterItem[]> {
  const key = `${sport}:${id}`;
  let pending = rostersCache.get(key);
  if (!pending) {
    pending = fetch(`${ESPN}/${sportPath(sport)}/teams/${id}/roster`).then(r => r.ok ? r.json() : Promise.reject()).then(data => (data.athletes ?? []).flatMap((row: RosterItem) => row.items ?? [row]));
    rostersCache.set(key, pending);
  }
  return pending;
}
function aliases(team: EspnTeam) {
  return [team.abbreviation, team.shortDisplayName, team.name, team.displayName, team.location]
    .filter((value): value is string => Boolean(value)).map(tidy).filter(Boolean);
}
function matchupTeams(game: string, rows: TeamRow[]): EspnTeam[] {
  const sides = game.split(/\s+@\s+/).map(tidy);
  if (sides.length !== 2) return [];
  return sides.map(side => rows.map(row => row.team).filter((team): team is EspnTeam => Boolean(team?.id && team.logos?.[0]?.href))
    .find(team => aliases(team).some(alias => alias === side || side === alias.split(' ').slice(-1)[0] || side.endsWith(` ${alias}`) || alias.endsWith(` ${side}`)))
  ).filter((team): team is EspnTeam => Boolean(team));
}

export async function loadPlayerPhotos(picks: BasePick[]): Promise<Map<string, PlayerImages>> {
  const result = new Map<string, PlayerImages>();
  await Promise.all([Sport.NBA, Sport.NHL].map(async sport => {
    const players = picks.filter(p => p.sport === sport);
    const missing = players.filter(p => {
      const key = cacheKey(sport, p.player);
      const cached = memory.get(key) ?? (() => { try { return localStorage.getItem(key) ?? undefined; } catch { return undefined; } })();
      if (cached) {
        const stored = memory.get(key) ?? (() => { try { return JSON.parse(localStorage.getItem(key) ?? 'null') as PlayerImages | null; } catch { return null; } })();
        if (stored) { memory.set(key, stored); result.set(p.id, stored); }
      }
      return !cached;
    });
    if (!missing.length) return;
    try {
      const rows = await teams(sport);
      const gameRows = new Map<string, EspnTeam[]>();
      for (const pick of missing) gameRows.set(pick.game, matchupTeams(pick.game, rows));
      const teamIds = new Set(Array.from(gameRows.values()).flat().map(team => team.id!).filter(Boolean));
      const rosters = new Map<string, RosterItem[]>();
      await Promise.all(Array.from(teamIds).map(async id => {
        try { rosters.set(id, await roster(sport, id)); } catch { rosters.set(id, []); }
      }));
      for (const pick of missing) {
        const playersTeams = (gameRows.get(pick.game) ?? []).filter(team => {
          const athletes = rosters.get(team.id!) ?? [];
          const wanted = tidy(pick.player);
          return athletes.some(a => tidy(a.fullName ?? a.displayName ?? '') === wanted);
        });
        // If a player's ESPN spelling differs, only accept a unique surname + first initial match.
        const exact = playersTeams[0];
        const gameTeamRows = gameRows.get(pick.game) ?? [];
        let found = exact;
        if (!found) {
          const wanted = tidy(pick.player).split(' ');
          const candidates = gameTeamRows.filter(team => (rosters.get(team.id!) ?? []).some(a => {
            const parts = tidy(a.fullName ?? a.displayName ?? '').split(' ');
            return parts[parts.length - 1] === wanted[wanted.length - 1] && parts[0]?.[0] === wanted[0]?.[0];
          }));
          if (candidates.length === 1) found = candidates[0];
        }
        const logo = found?.logos?.[0]?.href;
        const athlete = (found && (rosters.get(found.id!) ?? []).find(a => tidy(a.fullName ?? a.displayName ?? '') === tidy(pick.player)))
          ?? (() => {
            if (!found) return undefined;
            const wanted = tidy(pick.player).split(' ');
            return (rosters.get(found.id!) ?? []).find(a => {
              const parts = tidy(a.fullName ?? a.displayName ?? '').split(' ');
              return parts[parts.length - 1] === wanted[wanted.length - 1] && parts[0]?.[0] === wanted[0]?.[0];
            });
          })();
        const photo = typeof athlete?.headshot === 'string' ? athlete.headshot : athlete?.headshot?.href;
        if (!logo && !photo) continue;
        const key = cacheKey(sport, pick.player);
        const images = { photo, teamLogo: logo, athleteId: athlete?.id };
        memory.set(key, images);
        try { localStorage.setItem(key, JSON.stringify(images)); } catch { /* Cache remains in memory if storage is unavailable. */ }
        result.set(pick.id, images);
      }
    } catch { /* Show the team abbreviation fallback when ESPN cannot be reached. */ }
  }));
  return result;
}

export type PlayerGameContext = { date: string; venue?: 'home' | 'away'; opponent?: string };
export type PlayerGameContextResult = { games: PlayerGameContext[]; currentOpponent?: string };
const gameContextCache = new Map<string, { expiresAt: number; rows: PlayerGameContext[] }>();
const seasonForNow = () => { const now = new Date(); return now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear(); };
const contextKey = (sport: Sport, athleteId: string) => `rushprops.gamelog-context.v1.${sport}.${athleteId}.${seasonForNow()}`;

/** Resolve splits against the same real game history used by the prop's line. */
export async function loadPlayerGameContext(pick: BasePick): Promise<PlayerGameContextResult | null> {
  try {
    const rows = await teams(pick.sport);
    const gameTeams = matchupTeams(pick.game, rows);
    if (!gameTeams.length) return null;
    const rosters = await Promise.all(gameTeams.map(async team => ({
      team,
      players: await roster(pick.sport, team.id!).catch(() => []),
    })));
    const exactMatches = rosters.flatMap(({ team, players }) => players
      .filter(player => tidy(player.fullName ?? player.displayName ?? '') === tidy(pick.player))
      .map(player => ({ team, player })));
    const playerMatches = exactMatches.length ? exactMatches : rosters.flatMap(({ team, players }) => players
      .filter(player => {
        const actual = tidy(player.fullName ?? player.displayName ?? '').split(' ');
        const expected = tidy(pick.player).split(' ');
        return actual[actual.length - 1] === expected[expected.length - 1] && actual[0]?.[0] === expected[0]?.[0];
      })
      .map(player => ({ team, player })));
    if (playerMatches.length !== 1 || !playerMatches[0].player.id) return null;
    const { team, player } = playerMatches[0];
    const opponent = gameTeams.find(candidate => candidate.id !== team.id)?.abbreviation?.toUpperCase();
    const athleteId = String(player.id);
    const key = contextKey(pick.sport, athleteId);
    const cached = gameContextCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return { games: cached.rows, currentOpponent: opponent };
    try {
      const persisted = JSON.parse(localStorage.getItem(key) ?? 'null') as { expiresAt?: number; rows?: PlayerGameContext[] } | null;
      if (persisted?.expiresAt && persisted.expiresAt > Date.now() && Array.isArray(persisted.rows)) {
        gameContextCache.set(key, { expiresAt: persisted.expiresAt, rows: persisted.rows });
        return { games: persisted.rows, currentOpponent: opponent };
      }
    } catch { /* Use the live ESPN response when local storage is unavailable. */ }

    const sportPath = pick.sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
    const response = await fetch(`https://site.web.api.espn.com/apis/common/v3/sports/${sportPath}/athletes/${athleteId}/gamelog?season=${seasonForNow()}`);
    if (!response.ok) return { games: [], currentOpponent: opponent };
    const data = await response.json();
    const events = Object.values(data.events ?? {}) as Array<{ gameDate?: string; atVs?: string; homeAway?: string; opponent?: { abbreviation?: string } }>;
    const result = events.flatMap(event => {
      if (!event.gameDate) return [];
      const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(event.gameDate));
      const marker = String(event.atVs ?? event.homeAway ?? '').trim().toLowerCase();
      const venue: PlayerGameContext['venue'] = marker === 'vs' || marker === 'home' || marker.startsWith('vs ') ? 'home'
        : marker === '@' || marker === 'at' || marker === 'away' ? 'away' : undefined;
      return [{ date, venue, opponent: event.opponent?.abbreviation?.toUpperCase() }];
    });
    const expiresAt = Date.now() + 6 * 60 * 60 * 1000;
    gameContextCache.set(key, { expiresAt, rows: result });
    try { localStorage.setItem(key, JSON.stringify({ expiresAt, rows: result })); } catch { /* Session cache remains available. */ }
    return { games: result, currentOpponent: opponent };
  } catch { return null; }
}
